import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Armchair, ArrowLeft, BadgeCheck, CalendarCheck, DoorOpen,
  Fuel, Gauge, Heart, MapPin, MessageCircle, Palette, Phone, Settings2, Wrench,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RequestAppointmentModal } from "@/components/RequestAppointmentModal";
import { ChatDrawer, loadGuestChatForVehicle } from "@/components/ChatDrawer";
import FinanceCalculator from "@/components/FinanceCalculator";
import TradeInFlow from "@/components/TradeInFlow";
import { useAuth } from "@/context/AuthContext";
import { ToolsProvider } from "@/context/ToolsContext";
import { formatMiles, formatUSD, titleCase } from "@/lib/format";
import { BDC_PHONE_DISPLAY, BDC_PHONE_HREF } from "@/config/brand";

const MAP_PLACEHOLDER =
  "https://images.unsplash.com/photo-1708685889320-6c32b027f8b6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const SpecTile = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-4 border border-slate-200 rounded-xl bg-white">
    <div className="w-9 h-9 rounded-lg bg-brand-light text-brand flex items-center justify-center">
      <Icon size={17} />
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="mt-0.5 text-sm text-slate-900 font-medium">{value}</p>
    </div>
  </div>
);

const VehicleDetail = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  // Chat resumption state
  const [existingThread, setExistingThread] = useState(null); // {thread_id, source: 'guest'|'customer', token?}
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setExistingThread(null);
    setChatOpen(false);
    api
      .get(`/vehicles/${id}`)
      .then((r) => {
        setVehicle(r.data);
        setActiveIdx(0);
      })
      .catch(() => setVehicle(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "customer" || !vehicle) return;
    api.get("/me/saved-vehicles")
      .then((r) => setSaved(r.data.some((s) => s.vehicle_id === vehicle.id)))
      .catch(() => {});
  }, [isAuthenticated, user, vehicle]);

  // Detect an existing chat thread for this vehicle so returning visitors can
  // pick up their conversation. Guests: from localStorage.
  // Logged-in customers: from /me/leads.
  useEffect(() => {
    if (!vehicle) return;
    if (isAuthenticated && user?.role === "customer") {
      api.get("/me/leads")
        .then((r) => {
          const leadForVehicle = (r.data || []).find(
            (l) => l.vehicle_id === vehicle.id && l.chat_thread_id
          );
          if (leadForVehicle) {
            setExistingThread({ thread_id: leadForVehicle.chat_thread_id, source: "customer" });
          }
        })
        .catch(() => {});
    } else if (!isAuthenticated) {
      const stored = loadGuestChatForVehicle(vehicle.id);
      if (stored?.thread_id && stored?.guest_token) {
        setExistingThread({ thread_id: stored.thread_id, source: "guest", token: stored.guest_token });
      }
    }
  }, [vehicle, isAuthenticated, user]);

  const toggleSave = async () => {
    if (!isAuthenticated || user?.role !== "customer") {
      toast.info("Sign in as a customer to save vehicles.");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/me/saved-vehicles/${vehicle.id}`);
        setSaved(false);
        toast.success("Removed from saved vehicles.");
      } else {
        await api.post("/me/saved-vehicles", { vehicle_id: vehicle.id });
        setSaved(true);
        toast.success("Added to saved vehicles.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="h-[480px] bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="max-w-3xl mx-auto text-center py-24" data-testid="vehicle-not-found">
        <p className="font-display text-2xl text-slate-800">Vehicle not found</p>
        <p className="mt-2 text-slate-500">It may have been sold or removed from inventory.</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white">
          <Link to="/inventory">
            <ArrowLeft size={16} /> Back to inventory
          </Link>
        </Button>
      </div>
    );
  }

  const images = vehicle.images?.length ? vehicle.images : [
    "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1200&q=80",
  ];
  const activeImage = images[activeIdx];
  const dealer = vehicle.dealer;
  // Coming Soon dealers can be browsed but not booked against — customer
  // CTAs (Request Appointment / Save) get disabled + a status notice replaces
  // the primary action. The vehicle payload carries the flag directly (see
  // routes_marketplace.py `is_coming_soon` computation).
  const isComingSoon = !!vehicle.is_coming_soon || !!dealer?.is_coming_soon;
  // Phase 10 rollback — sold vehicles keep their detail URL (no 404) but the
  // customer-facing CTAs (appointment, save, chat, tools) are disabled and
  // replaced by a simple red SOLD ribbon.
  const isSold = vehicle.status === "sold";
  const ctasDisabled = isComingSoon || isSold;

  return (
    <ToolsProvider>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link
        to="/inventory"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand"
        data-testid="back-to-inventory-link"
      >
        <ArrowLeft size={14} /> Back to inventory
      </Link>

      {/* Sold vehicles keep their URL (no 404) but swap the primary CTA for
          a red bar so shoppers understand immediately. */}
      {isSold && (
        <div
          className="mt-4 rounded-xl border-2 border-rose-300 bg-rose-50 px-4 py-3 flex items-center gap-3"
          data-testid="vehicle-detail-sold-banner"
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 shrink-0">
            Sold
          </span>
          <p className="text-sm text-rose-900">
            <strong>This vehicle has been sold</strong>{vehicle.sold_at ? ` on ${vehicle.sold_at.slice(0, 10)}` : ""}
            . Browse similar cars in our{" "}
            <Link to="/inventory" className="underline font-semibold">inventory</Link>.
          </p>
        </div>
      )}

      {existingThread && (
        <div
          className="mt-4 rounded-xl border border-brand/20 bg-brand-light/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="continue-chat-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
              <MessageCircle size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                Continue your conversation
              </p>
              <p className="text-xs text-slate-600 truncate">
                You already reached out about this vehicle — pick up right where you left off.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setChatOpen(true)}
            className="bg-brand hover:bg-brand-hover text-white self-start sm:self-auto"
            data-testid="continue-chat-btn"
          >
            <MessageCircle size={15} /> Open chat
          </Button>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-12 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-7" data-testid="vehicle-gallery">
          <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
            <img src={activeImage} alt="" className="w-full h-full object-cover" />
            {vehicle.condition === "certified" && (
              <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-white/95 text-brand text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200">
                <BadgeCheck size={13} /> Certified Pre-Owned
              </span>
            )}
          </div>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`aspect-[4/3] rounded-lg overflow-hidden border transition-all ${
                  i === activeIdx ? "border-brand ring-2 ring-brand/30" : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Detail rail */}
        <div className="lg:col-span-5" data-testid="vehicle-info">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {vehicle.condition === "certified" ? "Certified Pre-Owned" : "Used"} · {vehicle.trim || titleCase(vehicle.body_style)}
            {vehicle.stock_number && (
              <>
                {" · "}
                <span className="font-mono text-slate-700" data-testid="vehicle-detail-stock-number">Stock #{vehicle.stock_number}</span>
              </>
            )}
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h1>

          <div className="mt-5 flex items-end gap-3">
            <span className="font-display text-4xl font-bold text-slate-900">
              {formatUSD(vehicle.price)}
            </span>
            {vehicle.msrp && vehicle.msrp > vehicle.price && (
              <span className="pb-1 text-sm text-slate-400 line-through">{formatUSD(vehicle.msrp)}</span>
            )}
            {vehicle.msrp && vehicle.msrp > vehicle.price && (
              <Badge className="bg-gold-light text-gold border border-gold/20 hover:bg-gold-light">
                Save {formatUSD(vehicle.msrp - vehicle.price)}
              </Badge>
            )}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button
              className="bg-gold hover:bg-gold-hover text-white h-12 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => setModalOpen(true)}
              disabled={ctasDisabled}
              title={isSold ? "This vehicle has been sold." : isComingSoon ? "This dealership isn't live yet — appointment booking opens soon." : undefined}
              data-testid="request-appt-button"
            >
              <CalendarCheck size={16} /> {isSold ? "Sold" : "Request appointment"}
            </Button>
            <Button
              variant="outline"
              className="border-brand text-brand hover:bg-brand hover:text-white h-12 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand"
              onClick={toggleSave}
              disabled={ctasDisabled}
              title={isSold ? "This vehicle has been sold." : isComingSoon ? "Saved vehicles will re-open when this dealership goes live." : undefined}
              data-testid="save-vehicle-btn"
            >
              <Heart size={16} className={saved ? "fill-current" : ""} /> {saved ? "Saved" : "Save"}
            </Button>
          </div>

          {isComingSoon && (
            <div
              className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50/70 px-4 py-3 flex items-start gap-3"
              data-testid="vehicle-coming-soon-notice"
            >
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 shrink-0">
                Coming Soon
              </span>
              <div className="text-xs sm:text-sm text-slate-700 leading-snug">
                <p className="font-semibold text-slate-900">This dealership is joining Belomotors shortly.</p>
                <p className="mt-1">
                  Appointment booking and saved vehicles open the day the dealership goes live. In the meantime,
                  call our BDC concierge for early access.
                </p>
              </div>
            </div>
          )}

          <a
            href={BDC_PHONE_HREF}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-brand"
            data-testid="vehicle-detail-bdc-phone-link"
          >
            <Phone size={14} className="text-brand" />
            Prefer to talk? Call our BDC concierge <span className="font-semibold text-brand">{BDC_PHONE_DISPLAY}</span>
          </a>

          {dealer && (
            <div className="mt-6 border border-slate-200 rounded-2xl overflow-hidden bg-white" data-testid="dealer-card">
              <div className="p-5">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sold by</p>
                <p className="mt-1 font-display text-lg font-semibold text-slate-900">{dealer.name}</p>
                <p className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1">
                  <MapPin size={13} /> {dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}
                </p>
                <p className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1">
                  <Phone size={13} /> {dealer.phone}
                </p>
                <Link
                  to={`/dealers/${dealer.id}`}
                  className="mt-4 inline-flex text-sm font-semibold text-brand hover:underline"
                >
                  View dealership profile →
                </Link>
              </div>
              <div className="border-t border-slate-100">
                <img src={MAP_PLACEHOLDER} alt="Map placeholder" className="w-full h-40 object-cover" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spec grid */}
      <section className="mt-14" data-testid="vehicle-specs">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Key specifications</h2>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <SpecTile icon={Gauge} label="Mileage" value={formatMiles(vehicle.mileage)} />
          <SpecTile icon={Settings2} label="Transmission" value={titleCase(vehicle.transmission)} />
          <SpecTile icon={Wrench} label="Drivetrain" value={vehicle.drivetrain?.toUpperCase()} />
          <SpecTile icon={Fuel} label="Fuel" value={titleCase(vehicle.fuel_type)} />
          <SpecTile
            icon={Fuel}
            label="MPG"
            value={vehicle.mpg_city && vehicle.mpg_highway ? `${vehicle.mpg_city} / ${vehicle.mpg_highway}` : "—"}
          />
          <SpecTile icon={Palette} label="Exterior" value={vehicle.exterior_color} />
          <SpecTile icon={Palette} label="Interior" value={vehicle.interior_color} />
          <SpecTile icon={Wrench} label="Engine" value={vehicle.engine} />
          <SpecTile icon={DoorOpen} label="Doors" value={String(vehicle.doors)} />
          <SpecTile icon={Armchair} label="Seats" value={String(vehicle.seats)} />
          <SpecTile icon={BadgeCheck} label="VIN" value={vehicle.vin} />
          <SpecTile icon={BadgeCheck} label="Condition" value={titleCase(vehicle.condition)} />
        </div>
      </section>

      {/* Description + features */}
      <section className="mt-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7" data-testid="vehicle-description">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Overview</h2>
          <Separator className="my-4" />
          <p className="text-slate-700 leading-relaxed">{vehicle.description}</p>
        </div>
        <div className="lg:col-span-5" data-testid="vehicle-features">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Features & equipment</h2>
          <Separator className="my-4" />
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
            {vehicle.features?.map((f) => (
              <li key={f} className="text-sm text-slate-700 flex items-start gap-2">
                <BadgeCheck size={14} className="text-brand mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Phase 7 — financing calculator + trade-in valuation
          These sit BELOW specs so the primary gold "Request Appointment" CTA
          stays the visual anchor. Collapsible: open on desktop, closed on
          mobile by default so scroll flow stays scannable. */}
      {!isSold && (
        <div className="mt-6 grid lg:grid-cols-2 gap-4">
          <FinanceCalculator vehicle={vehicle} variant="inline" />
          <TradeInFlow linkedVehicleId={vehicle.id} variant="inline" />
        </div>
      )}

      <RequestAppointmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        vehicle={vehicle}
        onLeadCreated={(data) => {
          if (data?.chat_thread_id) {
            if (isAuthenticated && user?.role === "customer") {
              setExistingThread({ thread_id: data.chat_thread_id, source: "customer" });
            } else if (data.guest_chat_token) {
              setExistingThread({
                thread_id: data.chat_thread_id,
                source: "guest",
                token: data.guest_chat_token,
              });
            }
          }
        }}
        onContinueChat={({ thread_id, guest_token, is_customer }) => {
          // Single source of truth for the drawer lives here in VehicleDetail.
          // The modal hands the freshly-minted thread back to us and closes;
          // we set existingThread + open the drawer in the next paint.
          setExistingThread({
            thread_id,
            source: is_customer ? "customer" : "guest",
            token: is_customer ? undefined : guest_token,
          });
          setChatOpen(true);
        }}
      />

      {existingThread && (
        <ChatDrawer
          threadId={existingThread.source === "customer" ? existingThread.thread_id : undefined}
          guestToken={existingThread.source === "guest" ? existingThread.token : undefined}
          open={chatOpen}
          onOpenChange={setChatOpen}
        />
      )}
    </div>
    </ToolsProvider>
  );
};

export default VehicleDetail;
